import React from 'react';
import PropTypes from 'prop-types';
import BudgetLineItemSection from './BudgetLineItemSection';
import useIsMobile from '../../shared/hooks/useIsMobile';

const legendChip = (background) => ({
  width: '24px', height: '24px', borderRadius: '6px',
  background, color: 'var(--bk-c-ffffff, #ffffff)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px',
});

const ExpensesSection = ({
  csrfToken,
  team,
  currentUser,
  offer,
  budget,
  calculated,
  updateBudget,
  createExpensesBudgetLineItem,
  createDeductionBudgetLineItem,
  createAncillaryExpensesBudgetLineItem,
  deleteBudgetLineItem,
  autoSaveBudgetLineItem,
  formatCalculatedNumber,
  financeCategories = [],
  onFinanceCategoryAdded,
  performers = [],
  onVisibilityChange,
  showCurrencyConversion,
  globalCurrency,
  reorderLineItem,
  onReorderLineItems,
  allLineItems,
  mobileOfferColumnOnly = false,
  // Read-only sibling V3Offer guarantee rows — forwarded to the first
  // (Expenses) BudgetLineItemSection only. Deductions and Ancillary
  // Expenses don't carry sibling rows. (O-2993)
  siblingRows = [],
  onSelectSiblingOffer
}) => {
  const isMobile = useIsMobile();
  const effectiveBudget = (isMobile && mobileOfferColumnOnly)
    ? {
      ...budget,
      // Force the Offer column visible — mobile-offer-only mode is meaningless
      // if a user-saved preference would also hide it.
      expenses_show_offer: true,
      deductions_show_offer: true,
      expenses_show_forecast: false,
      expenses_show_actuals: false,
      deductions_show_forecast: false,
      deductions_show_actuals: false,
      ancillary_expenses_show_forecast: false,
      ancillary_expenses_show_actuals: false,
    }
    : budget;
  const externalExpensesExpanded = effectiveBudget.expenses_section_expanded !== false;
  const deductionsExpanded = effectiveBudget.deductions_section_expanded !== false;
  const ancillaryExpensesExpanded = effectiveBudget.ancillary_expenses_section_expanded !== false;

  return (
    <div style={{
      backgroundColor: 'var(--bk-c-ffffff, #ffffff)',
      border: '1px solid var(--bk-c-e9eaeb, #E9EAEB)',
      borderRadius: '8px',
      boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
      padding: isMobile ? '12px' : '16px'
    }}>
      <h2 style={{
        fontSize: isMobile ? '18px' : '20px',
        fontWeight: '600',
        color: 'var(--bk-c-181d27, #181D27)',
        marginBottom: '16px',
        marginTop: 0
      }}>Expenses</h2>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '16px',
        fontSize: '13px',
        color: 'var(--bk-c-535862, #535862)',
      }}>
        <span>Tap to toggle:</span>
        {offer?.revenue_base === 'adjusted_gross' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={legendChip('var(--bk-c-1982c4, #1982C4)')}>
              <i className="fas fa-cut" aria-hidden="true" />
            </span>
            Deduct before split
          </span>
        )}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={legendChip('var(--bk-c-e8833a, #E8833A)')}>
            <i className="fas fa-eye-slash" aria-hidden="true" />
          </span>
          Don't show in offer
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <BudgetLineItemSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={effectiveBudget}
          calculated={calculated}
          updateBudget={updateBudget}
          createBudgetLineItem={createExpensesBudgetLineItem}
          deleteBudgetLineItem={deleteBudgetLineItem}
          autoSaveBudgetLineItem={autoSaveBudgetLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          expanded={externalExpensesExpanded}
          onToggleExpand={() => onVisibilityChange(
            'expenses_section_expanded', !externalExpensesExpanded)}
          sectionTitle="Expenses"
          sectionSubtitle="Show-related costs included on the offer/settlement and used in the split calculation. These reduce net revenue before the artist/venue split is applied."
          lineItemsKey="expense_line_items"
          lineItemsFilter={(item) => item.show_in_offer !== false}
          allowOfferVisibilityToggle={false}
          showInlineRowToggles
          showInlineVisibilityToggle
          calculatedSectionKey="expensesSection"
          calculatedTotalKey="externalTotal"
          calculatedForecastTotalKey="externalForecastTotal"
          calculatedActualTotalKey="externalActualTotal"
          calculatedRightTotalKey="externalRightTotal"
          calculatedRightForecastTotalKey="externalRightForecastTotal"
          calculatedRightActualTotalKey="externalRightActualTotal"
          addButtonText="Add expense"
          showOfferColumn={effectiveBudget.expenses_show_offer !== false}
          showForecastColumn={effectiveBudget.expenses_show_forecast !== false}
          showActualsColumn={effectiveBudget.expenses_show_actuals !== false}
          hideCollapsedColumns={isMobile && mobileOfferColumnOnly}
          onColumnVisibilityChange={(setting, value) =>
            onVisibilityChange(`expenses_${setting.replace('_column', '')}`, value)}
          financeCategories={financeCategories}
          onFinanceCategoryAdded={onFinanceCategoryAdded}
          performers={performers}
          sectionTabIndexOffset={5000}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={onReorderLineItems}
          allLineItems={allLineItems}
          siblingRows={siblingRows}
          onSelectSiblingOffer={onSelectSiblingOffer}
        />

        <BudgetLineItemSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={effectiveBudget}
          calculated={calculated}
          updateBudget={updateBudget}
          createBudgetLineItem={createDeductionBudgetLineItem}
          deleteBudgetLineItem={deleteBudgetLineItem}
          autoSaveBudgetLineItem={autoSaveBudgetLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          expanded={deductionsExpanded}
          onToggleExpand={() => onVisibilityChange(
            'deductions_section_expanded', !deductionsExpanded)}
          sectionTitle="Deductions"
          sectionSubtitle="Post-split deductions taken from the artist’s final settlement amount. These do not affect the split itself — only the final amount paid to the artist."
          lineItemsKey="deduction_line_items"
          calculatedSectionKey="deductionsSection"
          addButtonText="Add deduction"
          showOfferColumn={effectiveBudget.deductions_show_offer !== false}
          showForecastColumn={effectiveBudget.deductions_show_forecast !== false}
          showActualsColumn={effectiveBudget.deductions_show_actuals !== false}
          hideCollapsedColumns={isMobile && mobileOfferColumnOnly}
          onColumnVisibilityChange={(setting, value) =>
            onVisibilityChange(`deductions_${setting.replace('_column', '')}`, value)}
          financeCategories={financeCategories}
          onFinanceCategoryAdded={onFinanceCategoryAdded}
          performers={performers}
          sectionTabIndexOffset={5500}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={onReorderLineItems}
          allLineItems={allLineItems}
        />

        <BudgetLineItemSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={effectiveBudget}
          calculated={calculated}
          updateBudget={updateBudget}
          createBudgetLineItem={createAncillaryExpensesBudgetLineItem}
          deleteBudgetLineItem={deleteBudgetLineItem}
          autoSaveBudgetLineItem={autoSaveBudgetLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          expanded={ancillaryExpensesExpanded}
          onToggleExpand={() => onVisibilityChange(
            'ancillary_expenses_section_expanded', !ancillaryExpensesExpanded)}
          sectionTitle="Ancillary Expenses"
          sectionSubtitle="Costs that aren't shown on the offer or settlement and don't factor into any artist calculations. These are internal venue expenses, such as labor or security, unrelated to the artist deal."
          lineItemsKey="expense_line_items"
          lineItemsFilter={(item) => item.show_in_offer === false}
          allowOfferVisibilityToggle={false}
          showInlineRowToggles
          showInlineVisibilityToggle
          calculatedSectionKey="expensesSection"
          calculatedTotalSectionKey="showSummary"
          calculatedTotalKey="ancillaryExpenses"
          calculatedForecastTotalKey="ancillaryExpensesForecast"
          calculatedActualTotalKey="ancillaryExpensesActual"
          calculatedRightTotalKey="internalRightTotal"
          calculatedRightForecastTotalKey="internalRightForecastTotal"
          calculatedRightActualTotalKey="internalRightActualTotal"
          addButtonText="Add ancillary expense"
          hasOfferColumn={false}
          showForecastColumn={effectiveBudget.ancillary_expenses_show_forecast !== false}
          showActualsColumn={effectiveBudget.ancillary_expenses_show_actuals !== false}
          hideCollapsedColumns={isMobile && mobileOfferColumnOnly}
          onColumnVisibilityChange={(setting, value) =>
            onVisibilityChange(`ancillary_expenses_${setting.replace('_column', '')}`, value)}
          financeCategories={financeCategories}
          onFinanceCategoryAdded={onFinanceCategoryAdded}
          performers={performers}
          sectionTabIndexOffset={6000}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={onReorderLineItems}
          allLineItems={allLineItems}
        />
      </div>
    </div>
  );
};

ExpensesSection.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  budget: PropTypes.object.isRequired,
  calculated: PropTypes.object,
  updateBudget: PropTypes.func.isRequired,
  createExpensesBudgetLineItem: PropTypes.func.isRequired,
  createDeductionBudgetLineItem: PropTypes.func.isRequired,
  createAncillaryExpensesBudgetLineItem: PropTypes.func.isRequired,
  deleteBudgetLineItem: PropTypes.func.isRequired,
  autoSaveBudgetLineItem: PropTypes.func.isRequired,
  formatCalculatedNumber: PropTypes.func.isRequired,
  financeCategories: PropTypes.array,
  onFinanceCategoryAdded: PropTypes.func,
  performers: PropTypes.array,
  onVisibilityChange: PropTypes.func,
  showCurrencyConversion: PropTypes.bool,
  globalCurrency: PropTypes.object,
  reorderLineItem: PropTypes.func,
  onReorderLineItems: PropTypes.func,
  allLineItems: PropTypes.array,
  mobileOfferColumnOnly: PropTypes.bool,
  siblingRows: PropTypes.array,
  onSelectSiblingOffer: PropTypes.func
};

export default ExpensesSection;

